import React from "react";
import {
  getPerformanceSnapshot,
  savePerformanceTargets,
} from "../../services/performanceService";
import { getFlowAnalysis } from "../../services/flowAnalysisService";
import { convertCurrency } from "../../services/currencyService";
import useCurrencyRates from "../../hooks/useCurrencyRates";

// The four Devil's Quadrangle performance dimensions (FBPM Ch.8).
const DIMENSIONS = [
  { key: "TIME", label: "Time", color: "#b5761c", hint: "Cycle time — lower is better" },
  { key: "COST", label: "Cost", color: "#2e8b63", hint: "Labor cost per case — lower is better" },
  { key: "QUALITY", label: "Quality", color: "#7c55c6", hint: "Client / internal quality" },
  { key: "FLEXIBILITY", label: "Flexibility", color: "#2b79c2", hint: "Ability to absorb change" },
];

const CURRENCIES = ["PKR", "USD", "EUR", "GBP"];

function fmtNum(x) {
  return Number(x || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function minsToHM(min) {
  const total = Math.max(0, Math.round(Number(min) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h} h ${m} m` : `${m} m`;
}

// AS-IS computed value for a dimension. Primary source: the canonical (FBPM Ch.7,
// path-weighted) flow analysis — the SAME numbers the optimization module uses, so the
// two never disagree. Falls back to the legacy sequential snapshot when unavailable.
function asIsFor(dimKey, snapshot, currency, fa, rates) {
  if (fa) {
    if (dimKey === "TIME" && fa.cycle_time_minutes != null) {
      return {
        value: `${fmtNum(fa.cycle_time_minutes)} min`,
        alt: `≈ ${minsToHM(fa.cycle_time_minutes)}`,
        sub: `processing ${fmtNum(fa.processing_time_minutes)} · waiting ${fmtNum(fa.waiting_time_minutes)} · rework ${fmtNum(fa.rework_time_minutes)} (min) — path-weighted`,
        numeric: fa.cycle_time_minutes,
      };
    }
    if (dimKey === "COST" && fa.labor_cost_per_case != null) {
      const converted = convertCurrency(fa.labor_cost_per_case, "USD", currency, rates);
      const amount = converted != null ? converted : fa.labor_cost_per_case;
      return {
        value: `${currency} ${fmtNum(amount)}`,
        alt: currency === "USD" ? "" : `USD ${fmtNum(fa.labor_cost_per_case)}`,
        sub: "expected labor cost / case (path-weighted)",
        numeric: amount,
      };
    }
    if (dimKey === "QUALITY" && fa.quality_score != null) {
      const di = fa.dq_inputs || {};
      return {
        value: `${fmtNum(fa.quality_score)} / 100`,
        alt: "",
        sub: `estimate — rework ${fmtNum(di.rework_share_percent)}% of cycle · NVA steps ${fmtNum(di.nva_share_percent)}%`,
        numeric: fa.quality_score,
      };
    }
    if (dimKey === "FLEXIBILITY" && fa.flexibility_score != null) {
      const di = fa.dq_inputs || {};
      return {
        value: `${fmtNum(fa.flexibility_score)} / 100`,
        alt: "",
        sub: `estimate — ${di.parallel_task_count ?? 0} parallelized task(s) · ${di.substitutable_task_count ?? 0} with a skill-covering alternate job`,
        numeric: fa.flexibility_score,
      };
    }
  }
  const d = (snapshot && snapshot.dimensions) || {};
  if (dimKey === "TIME" && d.TIME) {
    return {
      value: `${fmtNum(d.TIME.cycle_time_minutes)} min`,
      alt: `≈ ${minsToHM(d.TIME.cycle_time_minutes)}`,
      sub: `processing ${fmtNum(d.TIME.processing_time_minutes)} · waiting ${fmtNum(d.TIME.waiting_time_minutes)} · rework ${fmtNum(d.TIME.rework_time_minutes)} (min)`,
      numeric: d.TIME.cycle_time_minutes,
    };
  }
  if (dimKey === "COST" && d.COST) {
    return {
      value: `${currency} ${fmtNum(d.COST.labor_cost_per_execution)}`,
      alt: "",
      sub: "labor cost / case",
      numeric: d.COST.labor_cost_per_execution,
    };
  }
  return { value: "—", alt: "", sub: "not yet measured", numeric: null };
}

const inputStyle = {
  width: "100%",
  padding: "7px 9px",
  border: "1px solid var(--gray-300)",
  borderRadius: 6,
  fontSize: 13,
  boxSizing: "border-box",
  background: "var(--color-surface)",
};
const labelStyle = {
  fontSize: 10.5,
  color: "var(--gray-500)",
  fontWeight: 700,
  letterSpacing: "0.03em",
  textTransform: "uppercase",
  marginBottom: 3,
  display: "block",
};
const sectionTag = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--gray-400)",
};

/**
 * Devil's Quadrangle performance panel.
 * - readOnly=true  → View Process: AS-IS + saved targets, no inputs.
 * - readOnly=false → Edit Process: AS-IS (auto-calculated) + editable targets.
 */
export default function PerformancePanel({ processId, isActive, readOnly = false }) {
  const [snapshot, setSnapshot] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState("");
  const [savedMsg, setSavedMsg] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({});
  const [currency, setCurrency] = React.useState("PKR");
  // Canonical (Ch.7) flow analysis — the single source the optimization module also uses.
  const [flow, setFlow] = React.useState(null);
  const { rates } = useCurrencyRates();

  const load = React.useCallback(async () => {
    if (!processId) return;
    setLoading(true);
    setError("");
    try {
      const [snap, faRes] = await Promise.all([
        getPerformanceSnapshot(processId),
        getFlowAnalysis(processId).catch(() => null),
      ]);
      setSnapshot(snap);
      setFlow(faRes);
      const seed = {};
      let cur = "PKR";
      (snap.targets || []).forEach((t) => {
        seed[t.dimension] = {
          id: t.id,
          measure_name: t.custom_kpi_name || "",
          baseline_value: t.baseline_value ?? "",
          target_value: t.target_value ?? "",
          threshold_value: t.threshold_value ?? "",
          custom_uom: t.custom_uom || "",
        };
        if (t.dimension === "COST" && CURRENCIES.includes(t.custom_uom)) cur = t.custom_uom;
      });
      setForm(seed);
      setCurrency(cur);
      setLoaded(true);
    } catch (e) {
      setError(e.message || "Failed to load performance data");
    } finally {
      setLoading(false);
    }
  }, [processId]);

  React.useEffect(() => {
    const shouldLoad = isActive === undefined ? true : isActive;
    if (shouldLoad && processId && !loaded && !loading) load();
  }, [isActive, processId, loaded, loading, load]);

  const setField = (dim, field, value) =>
    setForm((prev) => ({ ...prev, [dim]: { ...(prev[dim] || {}), [field]: value } }));

  const handleSave = async () => {
    setSaving(true);
    setSavedMsg("");
    setError("");
    try {
      const targets = Object.entries(form)
        .filter(
          ([, v]) =>
            v &&
            (v.target_value !== "" ||
              v.baseline_value !== "" ||
              v.threshold_value !== "" ||
              (v.measure_name && v.measure_name.trim())),
        )
        .map(([dim, v]) => ({
          id: v.id,
          dimension: dim,
          measure_name: v.measure_name || undefined,
          baseline_value: v.baseline_value === "" ? undefined : Number(v.baseline_value),
          target_value: v.target_value === "" ? undefined : Number(v.target_value),
          threshold_value: v.threshold_value === "" ? undefined : Number(v.threshold_value),
          custom_uom:
            dim === "COST" ? currency : v.custom_uom || undefined,
        }));
      await savePerformanceTargets(processId, targets);
      setSavedMsg("Performance targets saved.");
      setLoaded(false);
      await load();
    } catch (e) {
      setError(e.message || "Failed to save targets (admins only)");
    } finally {
      setSaving(false);
    }
  };

  const dq = (snapshot && snapshot.data_quality) || {};

  const unitFor = (dimKey, v) =>
    dimKey === "COST" ? currency : v.custom_uom || (dimKey === "TIME" ? "min" : "—");

  return (
    <div style={{ padding: "2px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Performance — Devil's Quadrangle</h3>
        <span style={{ fontSize: 12, color: "var(--gray-500)" }}>
          {readOnly ? "AS-IS estimate and saved targets" : "AS-IS is auto-calculated · you set the targets"}
        </span>
      </div>

      {loading && <p style={{ color: "var(--gray-500)" }}>Loading performance…</p>}
      {error && (
        <div style={{ margin: "10px 0", padding: "8px 12px", background: "var(--red-tint-50)", border: "1px solid var(--red-tint-200)", borderRadius: 8, color: "var(--red-ink-700)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {snapshot && (
        <>
          <div style={{ margin: "6px 0 16px", fontSize: 12, color: "var(--gray-500)", lineHeight: 1.5 }}>
            {flow
              ? "AS-IS values come from the book-correct (FBPM Ch.7, path-weighted) flow analysis — the same numbers the Process Optimization module uses. Quality & flexibility are 0–100 estimates computed from measured rework, NVA share, parallel structure and resource substitutability."
              : "First-cut estimate from task times & job rates (sequential sum — not yet parallelism/branch-weighted)."}{" "}
            <b>{dq.task_count}</b> tasks
            {dq.tasks_missing_processing_time ? ` · ${dq.tasks_missing_processing_time} missing a time` : ""}
            {dq.tasks_missing_responsible_job ? ` · ${dq.tasks_missing_responsible_job} missing a Responsible job` : ""}
            {dq.child_process_rows_excluded ? ` · ${dq.child_process_rows_excluded} sub-process step(s) excluded` : ""}.
            {!readOnly && " Fill task times & RACI on the Workflow tab to improve accuracy."}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(250px, 100%), 1fr))",
              gap: 16,
              alignItems: "stretch",
            }}
          >
            {DIMENSIONS.map((dim) => {
              const asis = asIsFor(dim.key, snapshot, currency, flow, rates);
              const v = form[dim.key] || {};
              const hasTarget =
                v && (v.target_value !== "" || v.baseline_value !== "" || v.threshold_value !== "" || v.measure_name);
              return (
                <div
                  key={dim.key}
                  style={{
                    border: "1px solid var(--gray-200)",
                    borderTop: `3px solid ${dim.color}`,
                    borderRadius: 10,
                    padding: "14px 16px",
                    background: "var(--color-surface)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Header — label and hint stacked (no overlap) */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: dim.color }}>{dim.label}</div>
                    <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 1 }}>{dim.hint}</div>
                  </div>

                  {/* AS-IS */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={sectionTag}>AS-IS (auto)</div>
                    <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1.15, marginTop: 2 }}>
                      {asis.value}
                    </div>
                    {asis.alt && <div style={{ fontSize: 12, color: "var(--gray-500)" }}>{asis.alt}</div>}
                    <div style={{ fontSize: 11, color: "var(--gray-400)", marginTop: 2 }}>{asis.sub}</div>
                  </div>

                  {/* Currency selector for COST (edit) */}
                  {dim.key === "COST" && !readOnly && (
                    <div style={{ marginBottom: 10 }}>
                      <label style={labelStyle}>Currency</label>
                      <select
                        style={inputStyle}
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                        title="The currency your job hourly rates are entered in (label only, no conversion)"
                      >
                        {CURRENCIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* TARGET */}
                  <div style={{ borderTop: "1px dashed var(--gray-200)", paddingTop: 10, marginTop: "auto" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={sectionTag}>Target</span>
                      {!readOnly && asis.numeric != null && (
                        <button
                          type="button"
                          onClick={() => setField(dim.key, "baseline_value", asis.numeric)}
                          style={{ fontSize: 10.5, color: dim.color, background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: 600 }}
                        >
                          use AS-IS ↑
                        </button>
                      )}
                    </div>

                    {readOnly ? (
                      hasTarget ? (
                        <div style={{ fontSize: 12.5, color: "var(--gray-700)", display: "grid", gap: 3 }}>
                          {v.measure_name && <div><b>Measure:</b> {v.measure_name}</div>}
                          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                            {v.baseline_value !== "" && <span><b>Baseline:</b> {fmtNum(v.baseline_value)}</span>}
                            {v.target_value !== "" && <span><b>Target:</b> {fmtNum(v.target_value)}</span>}
                            {v.threshold_value !== "" && <span><b>Threshold:</b> {fmtNum(v.threshold_value)}</span>}
                          </div>
                          <div style={{ color: "var(--gray-400)" }}>Unit: {unitFor(dim.key, v)}</div>
                        </div>
                      ) : (
                        <div style={{ fontSize: 12.5, color: "var(--gray-400)", fontStyle: "italic" }}>No target set.</div>
                      )
                    ) : (
                      <div style={{ display: "grid", gap: 8 }}>
                        <div>
                          <label style={labelStyle}>Measure</label>
                          <input style={inputStyle} value={v.measure_name || ""} placeholder="e.g. Average cycle time" onChange={(e) => setField(dim.key, "measure_name", e.target.value)} />
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                          <div>
                            <label style={labelStyle}>Baseline</label>
                            <input style={inputStyle} type="number" value={v.baseline_value ?? ""} onChange={(e) => setField(dim.key, "baseline_value", e.target.value)} />
                          </div>
                          <div>
                            <label style={labelStyle}>Target</label>
                            <input style={inputStyle} type="number" value={v.target_value ?? ""} onChange={(e) => setField(dim.key, "target_value", e.target.value)} />
                          </div>
                          <div>
                            <label style={labelStyle}>Threshold</label>
                            <input style={inputStyle} type="number" value={v.threshold_value ?? ""} onChange={(e) => setField(dim.key, "threshold_value", e.target.value)} />
                          </div>
                          <div>
                            <label style={labelStyle}>Unit</label>
                            {dim.key === "COST" ? (
                              <input style={{ ...inputStyle, background: "var(--gray-50)", color: "var(--gray-500)" }} value={currency} readOnly title="Set via the Currency selector above" />
                            ) : (
                              <input style={inputStyle} value={v.custom_uom || ""} placeholder={dim.key === "TIME" ? "min" : ""} onChange={(e) => setField(dim.key, "custom_uom", e.target.value)} />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {!readOnly && (
            <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 12 }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: "9px 20px",
                  background: saving ? "var(--accent-tint-300)" : "var(--accent-600)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: saving ? "default" : "pointer",
                }}
              >
                {saving ? "Saving…" : "Save targets"}
              </button>
              {savedMsg && <span style={{ color: "var(--emerald-ink-600)", fontSize: 13 }}>{savedMsg}</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
